import {
  FC,
  JSX,
  useState
} from "react";
import {
  Text,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  GestureResponderEvent,
  ActivityIndicator,
} from "react-native";
import { Svg } from "react-native-svg";

interface ICustomButtonProps {
  title: string;
  type?: "primary" | "default" | "text" | "filled";
  style?: ViewStyle;
  disabled?: boolean;
  className?: string;
  textStyle?: TextStyle;
  textClassName?: string;
  suffixIcon?: JSX.Element;
  suffixIconClass?: string;
  onPress?: ((event: GestureResponderEvent) => void) | undefined;
  loading?: boolean;
}

const btnBase: string = "flex flex-row items-center justify-center p-3.5 rounded-2xl transition-all duration-200";
const textBase: string = "text-base transition-all duration-200";
const btnClass: Record<string, string> = {
  primary: "bg-blue",
  default: "bg-transparent border border-gray-500",
  text: "bg-transparent",
  filled: "bg-blue-light",
};

const textClass: Record<string, string> = {
  primary: "text-white",
  default: "text-black active:text-blue-500",
  text: "text-blue-500",
  filled: "text-blue-500",
};

const CustomButton: FC<ICustomButtonProps> = ({
  title,
  type = "primary",
  style,
  disabled = false,
  className,
  textStyle,
  textClassName = "text-white",
  suffixIcon,
  suffixIconClass,
  onPress,
  loading = false,
}): JSX.Element => {
  const [isPressed, setIsPressed] = useState<boolean>(false);

  const textColorPressed: string = isPressed && type === "default" ? "text-blue-500" : textClassName;
  const textColor: string = disabled ? "text-gray" : textColorPressed;

  const togglePressed = () :void => setIsPressed(!isPressed);
  const btnClassByType = (): string => btnClass[type];
  const btnTextClassByType = (): string => textClass[type];

  const getLoaderColor = (): string => {
    switch (type) {
      case "primary":
        return "#9FABBE";
      case "default":
        return "#9FABBE";
      default:
        return "#9FABBE";
    }
  };

  const renderIcon = (): JSX.Element | null => {
    if (loading) {
      return (
        <ActivityIndicator
          size="small"
          color={getLoaderColor()}
          className="mr-2"
        />
      );
    }

    if (suffixIcon) {
      return (
        <Svg
          width={18}
          height={18}
          fill="none"
          stroke={isPressed ? "#1677FF" : "black"}
          className={`mr-2 transition-all duration-200 ${suffixIconClass}`}
        >
          {suffixIcon}
        </Svg>
      );
    }

    return null;
  };

  return (
    <TouchableOpacity
      style={style}
      onPress={onPress}
      className={`${className} ${btnBase} ${btnClassByType()} ${disabled ? "disabled-button" : ""}`}
      activeOpacity={0.7}
      disabled={disabled || loading}
      onPressIn={togglePressed}
      onPressOut={togglePressed}
    >
      {renderIcon()}
      <Text
        style={textStyle}
        className={`${textBase} ${btnTextClassByType()} ${textColor}`}
      >
        {title}
      </Text>
    </TouchableOpacity>
  );
};

export default CustomButton;
